import React, { useState, useEffect } from 'react';
import Layout from '../component/layout';
import { toastify } from '../../../utils/toast';
import { makeRequest2 } from '../../../utils/makeRequest';
import { getItem } from '../../../utils/localStorage';
import {
    getValueAddedServices,
    getFacilityServiceRequests,
    addServiceRequest,
    getUnitsForFacility,
    getVasVendors,
    // getEmployees,
    assignServiceRequest
} from '../../../utils/urls';

function Services() {
    const [activeTab, setActiveTab] = useState('all-requests');
    const [loading, setLoading] = useState(false);
    const [fetchingRequests, setFetchingRequests] = useState(false);

    // Service requests state
    const [serviceRequests, setServiceRequests] = useState([]);
    const [filteredRequests, setFilteredRequests] = useState([]);
    const [statusFilter, setStatusFilter] = useState('all');

    // Form states for raising request
    const [requestType, setRequestType] = useState('RESIDENT'); // RESIDENT or ADMIN
    const [selectedUnit, setSelectedUnit] = useState('');
    const [selectedService, setSelectedService] = useState('');
    const [description, setDescription] = useState('');
    const [preferredDate, setPreferredDate] = useState('');
    const [preferredTime, setPreferredTime] = useState('');
    const [attachment, setAttachment] = useState([]);

    // Units and services for dropdowns
    const [units, setUnits] = useState([]);
    const [services, setServices] = useState([]);
    const [fetchingUnits, setFetchingUnits] = useState(false);
    const [fetchingServices, setFetchingServices] = useState(false);

    // Vendors and employees for assignment
    const [vendors, setVendors] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [fetchingVendors, setFetchingVendors] = useState(false);
    const [fetchingEmployees, setFetchingEmployees] = useState(false);

    // Modal states
    const [viewModal, setViewModal] = useState({ show: false, request: null });
    const [assignModal, setAssignModal] = useState({ show: false, request: null });

    // Assignment form state
    const [assignmentType, setAssignmentType] = useState('vendor'); // 'vendor' or 'employee'
    const [selectedVendor, setSelectedVendor] = useState('');
    const [selectedEmployee, setSelectedEmployee] = useState('');
    const [assigning, setAssigning] = useState(false);

    // Status options
    const statusOptions = [
        { value: 'Pending', label: 'Pending', color: 'warning', icon: 'ti ti-clock' },
        { value: 'Awaiting', label: 'Awaiting', color: 'info', icon: 'ti ti-hourglass' },
        { value: 'In Progress', label: 'In Progress', color: 'primary', icon: 'ti ti-progress' },
        { value: 'Completed', label: 'Completed', color: 'success', icon: 'ti ti-check' },
        { value: 'Cancelled', label: 'Cancelled', color: 'danger', icon: 'ti ti-x' },
    ];

    const tabStatusMap = {
        'all-requests': 'all',
        'pending': 'Pending',
        'awaiting': 'Awaiting',
        'in-progress': 'In Progress',
        'completed': 'Completed',
        'cancelled': 'Cancelled'
    };

    const handleTabClick = (tabId) => {
        setActiveTab(tabId);
        if (tabId !== 'raise-request') {
            const mappedStatus = tabStatusMap[tabId] || 'all';
            setStatusFilter(mappedStatus);
            fetchServiceRequests().then((requests) => {
                if (requests) filterRequests(requests, mappedStatus);
            });
        }
        if (tabId === 'raise-request') {
            fetchServices();
        }
    };

    useEffect(() => {
        if (activeTab !== 'raise-request') {
            const mappedStatus = tabStatusMap[activeTab] || 'all';
            setStatusFilter(mappedStatus);
            fetchServiceRequests().then((requests) => {
                if (requests) filterRequests(requests, mappedStatus);
            });
        }
    }, [activeTab]);

    // Fetch service requests
    const fetchServiceRequests = async () => {
        try {
            setFetchingRequests(true);
            const facilityId = await getItem('selectedFacilityId');
            const response = await makeRequest2(`${getFacilityServiceRequests}/${facilityId}`, 'GET');

            console.log("Response:", response);

            if (response.success) {
                const requests = response.data.requests;
                setServiceRequests(requests);
                filterRequests(requests, statusFilter);
                return requests;
            } else {
                // toastify('Failed to fetch service requests', 'error');
            }
        } catch (error) {
            console.error('Error fetching service requests:', error);
            toastify(error.message, 'error');
        } finally {
            setFetchingRequests(false);
        }
    };

    // Fetch vendors
    const fetchVendors = async () => {
        setFetchingVendors(true);
        try {
            const facilityId = await getItem('selectedFacilityId');
            const response = await makeRequest2(`${getVasVendors}/${facilityId}`, 'GET')

            if (response.success) {
                setVendors(response.data.vendors);
            } else {
                toastify('Failed to fetch vendors', 'error');
            }
        } catch (error) {
            console.error('Error fetching vendors:', error);
            toastify(error.message, 'error');
        } finally {
            setFetchingVendors(false);
        }
    };

    // Filter requests by status
    const filterRequests = (requests, status) => {
        if (status === 'all') {
            setFilteredRequests(requests);
        } else {
            setFilteredRequests(requests.filter(req => req.status === status));
        }
    };

    const handleStatusFilterChange = (e) => {
        const status = e.target.value;
        setStatusFilter(status);
        filterRequests(serviceRequests, status);
    };

    const fetchUnitsForFacility = async () => {
        try {
            const facilityId = await getItem('selectedFacilityId')
            const response = await makeRequest2(`${getUnitsForFacility}/${facilityId}`, 'GET', {});

            if (response.success) {
                setUnits(response.data);
            } else {
                throw new Error('Failed to fetch units')
            }
        } catch (error) {
            console.log(error.message)
            toastify(error.message, 'error');
        }
    };

    const fetchServices = async () => {
        setFetchingServices(true);
        try {
            const facilityId = await getItem('selectedFacilityId');
            const response = await makeRequest2(`${getValueAddedServices}/${facilityId}`, 'GET');

            if (response.success) {
                const services = response.data?.services || [];
                const formattedServices = services.map(service => ({
                    ...service,
                    label: service.serviceName,
                    value: service._id
                }));
                setServices(formattedServices);
            } else {
                setServices([]);
                toastify(response.message, 'error');
            }
        } catch (error) {
            console.error('Error fetching services:', error);
            setServices([]);
            toastify(error.message, 'error');
        } finally {
            setFetchingServices(false);
        }
    };

    const handleRaiseRequest = async (e) => {
        e.preventDefault();

        if (!selectedUnit) {
            toastify('Please select a unit', 'error');
            return;
        }

        if (!selectedService) {
            toastify('Please select a service', 'error');
            return;
        }

        if (!description.trim()) {
            toastify('Please provide a description', 'error');
            return;
        }

        setLoading(true);

        try {
            const facilityId = await getItem('selectedFacilityId');

            const formData = new FormData();
            formData.append('unitId', selectedUnit);
            formData.append('serviceId', selectedService);
            formData.append('requestType', requestType);
            formData.append('description', description);

            if (preferredDate) {
                formData.append('preferredDate', preferredDate);
            }

            if (preferredTime) {
                formData.append('preferredTime', preferredTime);
            }

            if (attachment && attachment.length > 0) {
                formData.append('attachment', attachment[0]);
            }

            const response = await makeRequest2(`${addServiceRequest}/${facilityId}`, 'POST', formData);

            if (response.success) {
                toastify('Service request raised successfully', 'success');

                // Reset form
                setSelectedUnit('');
                setSelectedService('');
                setDescription('');
                setPreferredDate('');
                setPreferredTime('');
                setAttachment('');
                setRequestType('RESIDENT');

                // Switch to all requests tab and refresh
                setActiveTab('all-requests');
                fetchServiceRequests();
            } else {
                throw new Error(response.error);
            }
        } catch (error) {
            console.error('Error raising service request:', error);
            toastify(error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleAssignRequest = async () => {
        if (!selectedVendor) {
            toastify('Please select a vendor', 'error');
            return;
        }

        setAssigning(true);

        try {
            const facilityId = await getItem('selectedFacilityId');
            const assignData = {
                requestId: assignModal.request._id,
                vendorId: selectedVendor,
            };

            const response = await makeRequest2(`${assignServiceRequest}/${facilityId}`, 'POST', assignData);

            if (response.success) {
                toastify(`Request assigned to vendor successfully`, 'success');

                // Refresh requests
                await fetchServiceRequests();

                // Close modal and reset form
                closeAssignModal();
            } else {
                throw new Error(response.error || response.message || 'Failed to assign request');
            }
        } catch (error) {
            console.error('Error assigning request:', error);
            toastify(error.message, 'error');
        } finally {
            setAssigning(false);
        }
    };

    const handleViewRequest = (request) => {
        setViewModal({ show: true, request });
    };

    const closeViewModal = () => {
        setViewModal({ show: false, request: null });
    };

    // Assign modal handlers
    const handleAssignClick = (request) => {
        // Fetch vendors and employees when opening modal
        fetchVendors();
        // fetchEmployees();
        setAssignModal({ show: true, request });
        // Reset form
        setAssignmentType('vendor');
        setSelectedVendor('');
        setSelectedEmployee('');
    };

    const closeAssignModal = () => {
        setAssignModal({ show: false, request: null });
    };

    const getStatusBadge = (status) => {
        const statusObj = statusOptions.find(s => s.value === status) || statusOptions[0];

        return (
            <span className={`badge bg-${statusObj.color}  fs-6 fw-semibold`}>
                <i className={`${statusObj.icon} me-2 fs-6`}></i>
                {statusObj.label}
            </span>
        );
    };

    const getAssignedInfo = (request) => {
        if (request.assignee) {
            return (
                <span className="badge bg-info fs-6 fw-semibold">
                    <i className="ti ti-building-store me-2 fs-6"></i>
                    {request.assignee.name}
                </span>
            );
        } else if (request.assignedEmployee) {
            return (
                <span className="badge bg-info fs-6 fw-semibold">
                    <i className="ti ti-user me-2 fs-6"></i>
                    {request.assignedEmployee.fullName}
                </span>
            );
        }
        return null;
    };

    // Format date
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    useEffect(() => {
        if (activeTab !== 'raise-request') {
            fetchServiceRequests();
        }
    }, [activeTab]);

    useEffect(() => {
        fetchUnitsForFacility();
        fetchServices();
        fetchVendors();
    }, []);

    return (
        <Layout>
            <div className="row">
                <div className="col-sm-12">
                    {/* Tabs */}
                    <div className="card">
                        <div className="card-body py-0">
                            <ul className="nav nav-tabs profile-tabs" role="tablist">
                                <li className="nav-item">
                                    <button
                                        type="button"
                                        className={`nav-link cursor-pointer ${activeTab === 'all-requests' ? 'active' : ''}`}
                                        onClick={() => handleTabClick('all-requests')}
                                        role="tab"
                                    >
                                        <i className="ti ti-list me-2"></i>All Requests
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        type="button"
                                        className={`nav-link cursor-pointer ${activeTab === 'pending' ? 'active' : ''}`}
                                        onClick={() => handleTabClick('pending')}
                                        role="tab"
                                    >
                                        <i className="ti ti-clock me-2"></i>Pending
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        type="button"
                                        className={`nav-link cursor-pointer ${activeTab === 'awaiting' ? 'active' : ''}`}
                                        onClick={() => handleTabClick('awaiting')}
                                        role="tab"
                                    >
                                        <i className="ti ti-calendar-time me-2"></i>Awaiting
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        type="button"
                                        className={`nav-link cursor-pointer ${activeTab === 'in-progress' ? 'active' : ''}`}
                                        onClick={() => handleTabClick('in-progress')}
                                        role="tab"
                                    >
                                        <i className="ti ti-loader me-2"></i>In Progress
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        type="button"
                                        className={`nav-link cursor-pointer ${activeTab === 'completed' ? 'active' : ''}`}
                                        onClick={() => handleTabClick('completed')}
                                        role="tab"
                                    >
                                        <i className="ti ti-check me-2"></i>Completed
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        type="button"
                                        className={`nav-link cursor-pointer ${activeTab === 'cancelled' ? 'active' : ''}`}
                                        onClick={() => handleTabClick('cancelled')}
                                        role="tab"
                                    >
                                        <i className="ti ti-zoom-cancel me-2"></i>Cancelled
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        type="button"
                                        className={`nav-link cursor-pointer ${activeTab === 'raise-request' ? 'active' : ''}`}
                                        onClick={() => handleTabClick('raise-request')}
                                        role="tab"
                                    >
                                        <i className="ti ti-plus me-2"></i>Raise Request
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Tab Content */}
                    <div className="tab-content">
                        {/* All Requests Tab */}
                        {(activeTab === 'all-requests' || activeTab === 'pending' || activeTab === 'awaiting' || activeTab === 'in-progress' || activeTab === 'completed' || activeTab === 'cancelled') && (
                            <div className="tab-pane show active" role="tabpanel">
                                <div className="card">
                                    <div className="card-header">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <h5>
                                                {activeTab === 'all-requests' && 'All Service Requests'}
                                                {activeTab === 'pending' && 'Pending Requests'}
                                                {activeTab === 'awaiting' && 'Awaiting Requests'}
                                                {activeTab === 'in-progress' && 'In Progress Requests'}
                                                {activeTab === 'completed' && 'Completed Requests'}
                                                {activeTab === 'cancelled' && 'Cancelled Requests'}
                                            </h5>
                                            <div className="d-flex">
                                                <select
                                                    className="form-select form-select-sm me-2"
                                                    style={{ width: '150px' }}
                                                    value={statusFilter}
                                                    onChange={handleStatusFilterChange}
                                                >
                                                    <option value="all">All Status</option>
                                                    {statusOptions.map(option => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                <button
                                                    className="btn btn-primary btn-sm"
                                                    onClick={() => handleTabClick('raise-request')}
                                                >
                                                    <i className="ti ti-plus me-1"></i>
                                                    New Request
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        {fetchingRequests ? (
                                            <div className="text-center py-5">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <p className="mt-2">Loading requests...</p>
                                            </div>
                                        ) : filteredRequests.length === 0 ? (
                                            <div className="text-center py-5">
                                                <i className="ti ti-file-text" style={{ fontSize: '48px', color: '#ccc' }}></i>
                                                <h6 className="mt-3">No service requests found</h6>
                                                <p className="text-muted">Get started by raising a new request</p>
                                                <button
                                                    className="btn btn-primary"
                                                    onClick={() => handleTabClick('raise-request')}
                                                >
                                                    <i className="ti ti-plus me-2"></i>
                                                    Raise Request
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="table-responsive">
                                                <table className="table table-hover">
                                                    <thead>
                                                        <tr>
                                                            <th>#</th>
                                                            <th>Request No.</th>
                                                            <th>Unit</th>
                                                            <th>Service</th>
                                                            <th>Requested By</th>
                                                            <th>Assigned To</th>
                                                            <th>Status</th>
                                                            <th>Created</th>
                                                            <th>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {filteredRequests.map((request, index) => (
                                                            <tr key={request._id}>
                                                                <td>{index + 1}</td>
                                                                <td>
                                                                    <span className="text-primary">{request.requestNumber}</span>
                                                                </td>
                                                                <td>
                                                                    <strong>{request.unit?.unitNumber || request.unitNumber}</strong>
                                                                    <small className="d-block text-muted">
                                                                        {request.unitId?.name}
                                                                    </small>
                                                                </td>
                                                                <td>
                                                                    {request.serviceId?.serviceName}
                                                                </td>
                                                                <td>
                                                                    <div>
                                                                        <span className="badge bg-light text-dark">
                                                                            <i className={`ti ti-${request.requestType === 'ADMIN' ? 'user' : 'home'} me-1`}></i>
                                                                            {request.requestType}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    {getAssignedInfo(request)}
                                                                </td>
                                                                <td>{getStatusBadge(request.status)}</td>
                                                                <td>
                                                                    <small>{formatDate(request.createdAt)}</small>
                                                                </td>
                                                                <td>
                                                                    <button
                                                                        className="btn btn-sm btn-outline-info me-2"
                                                                        title="View Details"
                                                                        onClick={() => handleViewRequest(request)}
                                                                    >
                                                                        <i className="ti ti-eye"></i>
                                                                    </button>
                                                                    {request.status === 'Pending' && (
                                                                        <button
                                                                            className="btn btn-sm btn-outline-primary"
                                                                            title="Assign to Vendor/Employee"
                                                                            onClick={() => handleAssignClick(request)}
                                                                        >
                                                                            <i className="ti ti-user-plus"></i>
                                                                        </button>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Raise Request Tab */}
                        {activeTab === 'raise-request' && (
                            <div className="tab-pane show active" role="tabpanel">
                                <div className="card">
                                    <div className="card-header">
                                        <h5>Raise Service Request</h5>
                                    </div>
                                    <div className="card-body">
                                        <form onSubmit={handleRaiseRequest}>
                                            {/* Request Type Selection */}
                                            <div className="row mb-4">
                                                <div className="col-12">
                                                    <label className="form-label fw-semibold">Request Type</label>
                                                    <div className="d-flex gap-4">
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="radio"
                                                                name="requestType"
                                                                id="typeResident"
                                                                value="RESIDENT"
                                                                checked={requestType === 'RESIDENT'}
                                                                onChange={(e) => setRequestType(e.target.value)}
                                                            />
                                                            <label className="form-check-label" htmlFor="typeResident">
                                                                <i className="ti ti-home me-1"></i>
                                                                Resident Request
                                                            </label>
                                                        </div>
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="radio"
                                                                name="requestType"
                                                                id="typeAdmin"
                                                                value="ADMIN"
                                                                checked={requestType === 'ADMIN'}
                                                                onChange={(e) => setRequestType(e.target.value)}
                                                            />
                                                            <label className="form-check-label" htmlFor="typeAdmin">
                                                                <i className="ti ti-building-community me-1"></i>
                                                                Admin Request
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="row">
                                                {/* Unit Selection */}
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label fw-semibold">
                                                        Select Unit <span className="text-danger">*</span>
                                                    </label>
                                                    <select
                                                        className="form-select"
                                                        value={selectedUnit}
                                                        onChange={(e) => setSelectedUnit(e.target.value)}
                                                        disabled={loading || fetchingUnits}
                                                        required
                                                    >
                                                        <option value="">Choose Unit</option>
                                                        {units.map(unit => (
                                                            <option key={unit._id} value={unit._id}>
                                                                {unit.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* Service Selection */}
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label fw-semibold">
                                                        Select Service <span className="text-danger">*</span>
                                                    </label>
                                                    <select
                                                        className="form-select"
                                                        value={selectedService}
                                                        onChange={(e) => setSelectedService(e.target.value)}
                                                        disabled={loading || fetchingServices}
                                                        required
                                                    >
                                                        <option value="">Choose Service</option>
                                                        {services.map(service => (
                                                            <option key={service._id} value={service._id}>
                                                                {service.serviceName}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            {/* Description */}
                                            <div className="row mb-3">
                                                <div className="col-12">
                                                    <label className="form-label fw-semibold">
                                                        Description <span className="text-danger">*</span>
                                                    </label>
                                                    <textarea
                                                        className="form-control"
                                                        rows="4"
                                                        value={description}
                                                        onChange={(e) => setDescription(e.target.value)}
                                                        placeholder="Please describe the issue or request in detail"
                                                        disabled={loading}
                                                        required
                                                    ></textarea>
                                                </div>
                                            </div>

                                            {/* Priority and Schedule */}
                                            <div className="row">
                                                <div className="col-md-4 mb-3">
                                                    <label className="form-label fw-semibold">Preferred Date</label>
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        value={preferredDate}
                                                        onChange={(e) => setPreferredDate(e.target.value)}
                                                        min={new Date().toISOString().split('T')[0]}
                                                        disabled={loading}
                                                    />
                                                </div>

                                                <div className="col-md-4 mb-3">
                                                    <label className="form-label fw-semibold">Preferred Time</label>
                                                    <input
                                                        type="time"
                                                        className="form-control"
                                                        value={preferredTime}
                                                        onChange={(e) => setPreferredTime(e.target.value)}
                                                        disabled={loading}
                                                    />
                                                </div>
                                            </div>

                                            {/* Attachments */}
                                            <div className="row mb-4">
                                                <div className="col-12">
                                                    <label className="form-label fw-semibold">Attachments (Optional)</label>
                                                    <input
                                                        type="file"
                                                        className="form-control"
                                                        multiple
                                                        onChange={(e) => setAttachment(Array.from(e.target.files))}
                                                        disabled={loading}
                                                    />
                                                    <small className="text-muted">
                                                        Upload images or documents related to the request
                                                    </small>
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <div className="d-flex justify-content-end">
                                                <button
                                                    type="button"
                                                    className="btn btn-light me-2"
                                                    onClick={() => setActiveTab('all-requests')}
                                                    disabled={loading}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary px-4"
                                                    disabled={loading}
                                                >
                                                    {loading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                            Submitting...
                                                        </>
                                                    ) : (
                                                        'Submit Request'
                                                    )}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* View Request Modal */}
            {viewModal.show && viewModal.request && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1" role="dialog">
                    <div className="modal-dialog modal-lg" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    Service Request Details
                                    <small className="d-block text-muted">
                                        #{viewModal.request.requestNumber || viewModal.request._id.slice(-6)}
                                    </small>
                                </h5>
                                <button type="button" className="btn-close" onClick={closeViewModal}></button>
                            </div>
                            <div className="modal-body">
                                {/* Status and Assignment */}
                                <div className="row mb-4">
                                    <div className="col-md-6">
                                        <label className="fw-semibold">Status:</label>
                                        <div className="mt-1">
                                            {getStatusBadge(viewModal.request.status)}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="fw-semibold">Assigned To:</label>
                                        <div>
                                            {viewModal.request.assignee && (
                                                <span className="p-3 fs-5">
                                                    <div>
                                                        <strong>Vendor: {viewModal.request.assignee.name}</strong>
                                                        {viewModal.request.assignee.phone && (
                                                            <div className="mt-2 fs-6">
                                                                Contact: {viewModal.request.assignee.phone}
                                                            </div>
                                                        )}
                                                    </div>
                                                </span>
                                            )}
                                            {viewModal.request.assignedEmployee && (
                                                <span className="badge bg-info p-2">
                                                    <i className="ti ti-user me-1"></i>
                                                    Employee: {viewModal.request.assignedEmployee.fullName}
                                                    {viewModal.request.assignedEmployee.role && (
                                                        <span className="d-block small">
                                                            Role: {viewModal.request.assignedEmployee.role}
                                                        </span>
                                                    )}
                                                </span>
                                            )}
                                            {!viewModal.request.assignee && !viewModal.request.assignedEmployee && (
                                                <span className="text-muted">Not assigned</span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="row mb-3">
                                    <div className="col-md-4">
                                        <label className="fw-semibold">Customer Name:</label>
                                        <p className="mb-0">{viewModal.request.customer?.fullName}</p>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="fw-semibold">Phone Number:</label>
                                        <p className="mb-0">{viewModal.request.customer?.phoneNumber}</p>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="fw-semibold">Email:</label>
                                        <p className="mb-0">{viewModal.request.customer?.email}</p>
                                    </div>
                                </div>

                                {/* Unit and Service */}
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="fw-semibold">Unit:</label>
                                        <p className="mb-0">
                                            {viewModal.request.unitId?.name}
                                        </p>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="fw-semibold">Service:</label>
                                        <p className="mb-0">{viewModal.request.serviceId?.serviceName}</p>
                                    </div>
                                </div>

                                {/* Request Info */}
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="fw-semibold">Request Type:</label>
                                        <p className="mb-0">
                                            <span className="badge bg-light text-dark">
                                                <i className={`ti ti-${viewModal.request.requestType === 'ADMIN' ? 'user' : 'home'} me-1`}></i>
                                                {viewModal.request.requestType}
                                            </span>
                                        </p>
                                    </div>
                                </div>

                                {/* Description */}
                                <div className="row mb-3">
                                    <div className="col-12">
                                        <label className="fw-semibold">Description:</label>
                                        <p className="mb-0 p-3 bg-light rounded">
                                            {viewModal.request.description}
                                        </p>
                                    </div>
                                </div>

                                {viewModal.request.attachment && (
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            <label className="fw-semibold">Attachment:</label>
                                            <div className="p-3 bg-light rounded text-center">
                                                <img
                                                    src={viewModal.request.attachment}
                                                    alt="Attachment"
                                                    style={{ maxWidth: '100%', maxHeight: '250px' }}
                                                    className="mb-2"
                                                />
                                                <br />
                                                <a
                                                    href={viewModal.request.attachment}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-sm btn-outline-primary"
                                                >
                                                    Open Full Image
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Preferred Schedule */}
                                {(viewModal.request.date || viewModal.request.time) && (
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            <label className="fw-semibold">Preferred Schedule:</label>
                                            <p className="mb-0">
                                                {viewModal.request.date && (
                                                    <> <strong>Date: </strong>{new Date(viewModal.request.date).toLocaleDateString()}</>
                                                )}
                                                <br />
                                                <br />
                                                {viewModal.request.time && (
                                                    <> <strong>At: </strong> {viewModal.request.time}</>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Timeline */}
                                <div className="row mt-4">
                                    <div className="col-12">
                                        <label className="fw-semibold mb-2">Timeline:</label>
                                        <div className="timeline">
                                            <div className="timeline-item">
                                                <small className="text-muted d-block">
                                                    Created: {formatDate(viewModal.request.createdAt)}
                                                </small>
                                            </div>
                                            {viewModal.request.statusHistory?.map((history, idx) => (
                                                <div key={idx} className="timeline-item mt-2">
                                                    <small className="d-block">
                                                        Status changed to <span className="badge bg-info">{history.status}</span>
                                                    </small>
                                                    <small className="text-muted">
                                                        {formatDate(history.updatedAt)} by {history.updatedByName || 'System'}
                                                    </small>
                                                    {history.comment && (
                                                        <small className="d-block text-muted mt-1">
                                                            Comment: {history.comment}
                                                        </small>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Additional Details */}
                                {(viewModal.request.quote ||
                                    viewModal.request.notes ||
                                    viewModal.request.denyReason ||
                                    viewModal.request.residentAcceptedAt ||
                                    viewModal.request.residentDeniedAt) && (
                                        <div className="row mt-4">
                                            <div className="col-12">
                                                <label className="fw-semibold mb-2">Vendor Quote:</label>

                                                <div className="p-3 bg-light rounded">

                                                    {viewModal.request.quote && (
                                                        <div className="mb-2">
                                                            <strong>Quote:</strong> KES {viewModal.request.quote}
                                                        </div>
                                                    )}

                                                    {viewModal.request.notes && (
                                                        <div className="mb-2">
                                                            <strong>Notes:</strong> {viewModal.request.notes}
                                                        </div>
                                                    )}

                                                    {viewModal.request.denyReason && (
                                                        <div className="mb-2">
                                                            <strong>Deny Reason:</strong> {viewModal.request.denyReason}
                                                        </div>
                                                    )}

                                                    {viewModal.request.residentAcceptedAt && (
                                                        <div className="mb-2">
                                                            <strong>Accepted At:</strong>{" "}
                                                            {formatDate(viewModal.request.residentAcceptedAt)}
                                                        </div>
                                                    )}

                                                    {viewModal.request.residentDeniedAt && (
                                                        <div className="mb-2">
                                                            <strong>Denied At:</strong>{" "}
                                                            {formatDate(viewModal.request.residentDeniedAt)}
                                                        </div>
                                                    )}

                                                </div>
                                            </div>
                                        </div>
                                    )}

                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeViewModal}>
                                    Close
                                </button>
                                {viewModal.request.status === 'Pending' && (
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => {
                                            closeViewModal();
                                            handleAssignClick(viewModal.request);
                                        }}
                                    >
                                        <i className="ti ti-user-plus me-2"></i>
                                        Assign Request
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Assign Request Modal */}
            {assignModal.show && assignModal.request && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1" role="dialog">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <i className="ti ti-user-plus me-2"></i>
                                    Assign Service Request
                                </h5>
                                <button type="button" className="btn-close" onClick={closeAssignModal}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="fw-semibold">Request:</label>
                                    <p className="mb-0 bg-light p-2 rounded">
                                        #{assignModal.request.requestNumber || assignModal.request._id.slice(-6)} - {assignModal.request.serviceId?.serviceName}
                                    </p>
                                </div>

                                {/* Assignment Type Selection */}
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Assign To</label>
                                    <div className="d-flex gap-3">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="assignmentType"
                                                id="assignVendor"
                                                value="vendor"
                                                checked={assignmentType === 'vendor'}
                                                onChange={(e) => {
                                                    setAssignmentType(e.target.value);
                                                    setSelectedVendor('');
                                                }}
                                            />
                                            <label className="form-check-label" htmlFor="assignVendor">
                                                <i className="ti ti-building-store me-1"></i>
                                                Vendor
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="assignmentType"
                                                id="assignEmployee"
                                                value="employee"
                                                checked={assignmentType === 'employee'}
                                                onChange={(e) => {
                                                    setAssignmentType(e.target.value);
                                                    setSelectedEmployee('');
                                                }}
                                            />
                                            <label className="form-check-label" htmlFor="assignEmployee">
                                                <i className="ti ti-user me-1"></i>
                                                Employee
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                {/* Vendor Selection */}
                                {assignmentType === 'vendor' && (
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">
                                            Select Vendor <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className="form-select"
                                            value={selectedVendor}
                                            onChange={(e) => setSelectedVendor(e.target.value)}
                                            disabled={fetchingVendors}
                                        >
                                            <option value="">Choose a vendor</option>
                                            {vendors.map(vendor => (
                                                <option key={vendor._id} value={vendor._id}>
                                                    {vendor.name} {vendor.contactPerson ? `- ${vendor.contactPerson}` : ''}
                                                </option>
                                            ))}
                                        </select>
                                        {fetchingVendors && (
                                            <small className="text-muted">
                                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                Loading vendors...
                                            </small>
                                        )}
                                    </div>
                                )}

                                {/* Employee Selection */}
                                {assignmentType === 'employee' && (
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">
                                            Select Employee <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className="form-select"
                                            value={selectedEmployee}
                                            onChange={(e) => setSelectedEmployee(e.target.value)}
                                            disabled={fetchingEmployees}
                                        >
                                            <option value="">Choose an employee</option>
                                            {employees.map(employee => (
                                                <option key={employee._id} value={employee._id}>
                                                    {employee.fullName} {employee.role ? `- ${employee.role}` : ''}
                                                </option>
                                            ))}
                                        </select>
                                        {fetchingEmployees && (
                                            <small className="text-muted">
                                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                Loading employees...
                                            </small>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-light"
                                    onClick={closeAssignModal}
                                    disabled={assigning}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleAssignRequest}
                                    disabled={assigning || (assignmentType === 'vendor' && !selectedVendor) || (assignmentType === 'employee' && !selectedEmployee)}
                                >
                                    {assigning ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            Assigning...
                                        </>
                                    ) : (
                                        <>
                                            <i className="ti ti-check me-2"></i>
                                            Assign Request
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
}

export default Services;